import React, { useState, useEffect, useRef } from 'react';
import DashboardService from '../services/DashboardService';
import VariablePublishEditor from './VariablePublishEditor';
import VariableSubscribeEditor from './VariableSubscribeEditor';
import FunctionCallEditor from './FunctionCallEditor';
import './EventTabPanel.css';

/**
 * 이벤트 탭 패널
 * 차트의 클릭 이벤트 시 변수 발행(publish)과 구독(subscribe) 설정을 관리
 */
const EventTabPanel = ({ config, chartType, onChange, tableColumns = [], sql = '' }) => {
  // 섹션 접기/펼치기 상태
  const [expandedSections, setExpandedSections] = useState({
    publish: true,
    subscribe: true,
    dashboard: true,
    function: true
  });
  
  // 로컬 상태 관리
  const [publishVariables, setPublishVariables] = useState([]);
  const [subscribedVariables, setSubscribedVariables] = useState([]);
  const [clearVariablesOnDeselect, setClearVariablesOnDeselect] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState(() => {
    const od = config?.on_click?.openDashboard;
    // dashboardId 또는 dashboards[0].id를 읽어옴 (멀티 탭 호환)
    const extractedDashboardId = od?.dashboardId || 
      (Array.isArray(od?.dashboards) && od.dashboards.length > 0 ? od.dashboards[0]?.id : '') || 
      '';
    return od ? {
      enabled: true,
      dashboardId: extractedDashboardId,
      size: od.size || { width: '80%', height: '70%' },
      position: od.position || 'center',
      sizeMode: od.sizeMode || 'fixed', // 'fixed' | 'auto'
      transparentMode: od.transparentMode || false // 투명 대시보드 모드
    } : {
      enabled: false,
      dashboardId: '',
      size: { width: '80%', height: '70%' },
      position: 'center',
      sizeMode: 'fixed',
      transparentMode: false
    };
  });
  const [functionCallConfig, setFunctionCallConfig] = useState(() => {
    const cf = config?.on_click?.callFunction;
    return cf ? {
      enabled: true,
      functionName: cf.functionName || '',
      parameters: cf.parameters || {},
      confirmMessage: cf.confirmMessage || '',
      successMessage: cf.successMessage || '',
      refreshAfter: !!cf.refreshAfter,
      // 단일 note 설정으로 단순화 (legacy inputFields[0] 지원)
      note: cf.note || (Array.isArray(cf.inputFields) && cf.inputFields.length > 0 ? {
        enabled: true,
        label: cf.inputFields[0].label || '처리 메모',
        placeholder: cf.inputFields[0].placeholder || '',
        required: !!cf.inputFields[0].required,
        default: cf.inputFields[0].default || '',
        type: cf.inputFields[0].type || 'textarea'
      } : { enabled: false, label: '처리 메모', placeholder: '', required: false, default: '', type: 'textarea' })
    } : {
      enabled: false,
      functionName: '',
      parameters: {},
      confirmMessage: '',
      successMessage: '',
      refreshAfter: false,
      note: { enabled: false, label: '처리 메모', placeholder: '', required: false, default: '', type: 'textarea' }
    };
  });
  // 사용자 대시보드 목록 로딩 상태
  const [availableDashboards, setAvailableDashboards] = useState([]);
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [dashboardsError, setDashboardsError] = useState(null);
  
  // 초기 마운트 추적 및 원본 보존용 레퍼런스
  const [isInitialized, setIsInitialized] = useState(false);
  const originalOnClickRef = useRef(null);
  const originalSubscribedRef = useRef(null);
  const originalOpenDashboardRef = useRef(null);
  const originalCallFunctionRef = useRef(null);

  // 사용자 편집 여부 플래그
  const [touchedPublish, setTouchedPublish] = useState(false);
  const [touchedSubscribed, setTouchedSubscribed] = useState(false);
  const [touchedOpenDashboard, setTouchedOpenDashboard] = useState(false);
  const [touchedCallFunction, setTouchedCallFunction] = useState(false);
  
  // onChange 함수 참조 저장
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // 차트별 기본 col 개수 (핵심 매핑 개수) 계산
  const getBaseCols = (type) => {
    const t = (type || '').toLowerCase();
    switch (t) {
      case 'pie': return 2; // name, value
      case 'line':
      case 'bar': return 2; // x, y (멀티는 3이지만 안전 폴백: extra1→col3)
      case 'scatter': return 3; // x, y, size?
      case 'heatmap': return 3; // x, y, value
      case 'gauge': return 1; // value
      case 'badge': return 1; // value
      case 'battery_grid': return 4; // batt_id, soc, soh, state
      default: return 2;
    }
  };

  // $event.extraN → $event.colK로 변환
  const normalizeExpression = (expression, type) => {
    if (typeof expression !== 'string') return expression;
    const match = expression.match(/\$event\.extra(\d+)/i);
    if (!match) return expression;
    const n = parseInt(match[1], 10);
    const base = getBaseCols(type);
    const k = base + n; // extra1→col(base+1), extra2→col(base+2)
    return expression.replace(/\$event\.extra\d+/gi, `$event.col${k}`);
  };
  
  // 안전한 딥 클론 헬퍼 (보존 목적)
  const deepClone = (obj) => {
    try { return obj == null ? obj : JSON.parse(JSON.stringify(obj)); } catch (_) { return obj; }
  };

  // config prop으로부터 초기화 (첫 로드 시만)
  useEffect(() => {
    if (config && !isInitialized) {
      // 원본 on_click / subscribed_variables 보존
      originalOnClickRef.current = deepClone(config.on_click) || null;
      originalSubscribedRef.current = deepClone(config.subscribed_variables) || null;

      // on_click.publish 파싱
      if (config.on_click?.publish) {
        const vars = Object.entries(config.on_click.publish).map(([name, expression]) => ({
          id: Math.random().toString(36).substr(2, 9),
          name,
          expression: normalizeExpression(expression, chartType),
          type: detectExpressionType(expression)
        }));
        setPublishVariables(vars);
      }
      
      // on_click.clearVariablesOnDeselect 파싱
      if (config.on_click?.clearVariablesOnDeselect !== undefined) {
        setClearVariablesOnDeselect(!!config.on_click.clearVariablesOnDeselect);
      }
      
      // subscribed_variables 파싱
      if (config.subscribed_variables) {
        const vars = Object.entries(config.subscribed_variables).map(([name, settings]) => ({
          id: Math.random().toString(36).substr(2, 9),
          name,
          defaultValue: settings.default || ''
        }));
        setSubscribedVariables(vars);
      }
      
      // openDashboard 설정 파싱 (state 초기값에 반영되어 있어도 원본 보존)
      if (config.on_click?.openDashboard) {
        originalOpenDashboardRef.current = deepClone(config.on_click.openDashboard);
        const od = config.on_click.openDashboard;
        // dashboardId 또는 dashboards[0].id를 읽어옴 (멀티 탭 호환)
        const extractedDashboardId = od.dashboardId || 
          (Array.isArray(od.dashboards) && od.dashboards.length > 0 ? od.dashboards[0]?.id : '') || 
          '';
        setDashboardConfig(prev => ({
          ...prev,
          enabled: true,
          dashboardId: extractedDashboardId || prev.dashboardId,
          size: od.size || prev.size,
          position: od.position || prev.position,
          sizeMode: od.sizeMode || prev.sizeMode,
          transparentMode: od.transparentMode || false
        }));
      }

      // callFunction 설정 파싱 (state 초기값에 반영되어 있어도 원본 보존)
      if (config.on_click?.callFunction) {
        originalCallFunctionRef.current = deepClone(config.on_click.callFunction);
        setFunctionCallConfig(prev => ({
          ...prev,
          enabled: true,
          functionName: config.on_click.callFunction.functionName || prev.functionName,
          parameters: config.on_click.callFunction.parameters || prev.parameters,
          confirmMessage: config.on_click.callFunction.confirmMessage || prev.confirmMessage,
          successMessage: config.on_click.callFunction.successMessage || prev.successMessage,
          refreshAfter: !!config.on_click.callFunction.refreshAfter,
          note: config.on_click.callFunction.note || (Array.isArray(config.on_click.callFunction.inputFields) && config.on_click.callFunction.inputFields.length > 0 ? {
            enabled: true,
            label: config.on_click.callFunction.inputFields[0].label || '처리 메모',
            placeholder: config.on_click.callFunction.inputFields[0].placeholder || '',
            required: !!config.on_click.callFunction.inputFields[0].required,
            default: config.on_click.callFunction.inputFields[0].default || '',
            type: config.on_click.callFunction.inputFields[0].type || 'textarea'
          } : prev.note)
        }));
      }

      setIsInitialized(true);
    }
  }, [config, isInitialized]);

  // 대시보드 목록 로드 (대시보드 섹션을 처음 펼칠 때 1회 로드)
  useEffect(() => {
    const shouldLoad = expandedSections.dashboard && availableDashboards.length === 0 && !dashboardsLoading;
    if (!shouldLoad) return;
    (async () => {
      try {
        setDashboardsLoading(true);
        setDashboardsError(null);
        const resp = await DashboardService.getUserDashboards();
        if (resp && resp.success && Array.isArray(resp.data?.dashboards)) {
          setAvailableDashboards(resp.data.dashboards);
        } else {
          setAvailableDashboards([]);
          setDashboardsError(resp?.error || '대시보드 목록을 불러오지 못했습니다.');
        }
      } catch (e) {
        setAvailableDashboards([]);
        setDashboardsError('대시보드 목록 로드 중 오류가 발생했습니다.');
      } finally {
        setDashboardsLoading(false);
      }
    })();
  }, [expandedSections.dashboard, availableDashboards.length, dashboardsLoading]);
  
  // 표현식 타입 감지
  const detectExpressionType = (expression) => {
    if (typeof expression !== 'string') return 'direct';
    if (expression.includes('?') && expression.includes(':')) return 'conditional';
    if (expression.startsWith('$event.')) return 'expression';
    return 'direct';
  };
  
  // 섹션 토글
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // 변경사항을 부모 컴포넌트로 전달 (사용자 편집 시에만)
  useEffect(() => {
    // 초기화가 완료되지 않았거나 사용자가 아무 것도 수정하지 않았으면 전달하지 않음
    if (!isInitialized) return;

    const anyTouched = touchedPublish || touchedSubscribed || touchedOpenDashboard || touchedCallFunction;
    if (!anyTouched) return;

    // 베이스를 원본으로부터 복원한 뒤, touched된 항목만 덮어쓰기
    const originalOnClick = deepClone(originalOnClickRef.current) || null;
    const originalSubscribed = deepClone(originalSubscribedRef.current) || null;

    // on_click 구성
    let nextOnClick = originalOnClick ? deepClone(originalOnClick) : {};

    if (touchedPublish) {
      // 사용자가 편집한 publish를 새로 구성 (키 삭제도 반영)
      const nextPublish = {};
      publishVariables.forEach(v => {
        if (!v.name) return;
        if (typeof v.expression === 'string') {
          nextPublish[v.name] = normalizeExpression(v.expression, chartType);
        } else if (v.expression == null) {
          nextPublish[v.name] = null; // null 값 보존
        } else {
          nextPublish[v.name] = v.expression; // 기타 타입 방어적 보존
        }
      });
      if (Object.keys(nextPublish).length > 0) {
        nextOnClick.publish = nextPublish;
        // clearVariablesOnDeselect 옵션 추가
        if (clearVariablesOnDeselect) {
          nextOnClick.clearVariablesOnDeselect = true;
        }
      } else {
        // 비어있으면 제거
        if (nextOnClick && nextOnClick.publish) delete nextOnClick.publish;
        if (nextOnClick && nextOnClick.clearVariablesOnDeselect) delete nextOnClick.clearVariablesOnDeselect;
      }
    }

    if (touchedOpenDashboard) {
      // openDashboard 병합: 원본을 기준으로 size/position과 단일 선택값을 반영
      const originalOD = originalOnClick?.openDashboard || originalOpenDashboardRef.current || {};
      const nextOD = { ...(originalOD || {}) };

      // 크기 모드 업데이트
      if (dashboardConfig?.sizeMode) nextOD.sizeMode = dashboardConfig.sizeMode;
      
      // 크기/위치 업데이트 (사용자 값 우선)
      if (dashboardConfig?.size) nextOD.size = dashboardConfig.size;
      if (dashboardConfig?.position) nextOD.position = dashboardConfig.position;
      
      // 투명 대시보드 모드 업데이트
      nextOD.transparentMode = dashboardConfig.transparentMode || false;

      // 단일 선택이 있는 경우, dashboards 배열만 사용 (표준)
      if (dashboardConfig?.dashboardId) {
        // dashboards 배열을 표준으로 사용
        nextOD.dashboards = [{ id: dashboardConfig.dashboardId }];
        // 레거시 dashboardId 필드 제거
        delete nextOD.dashboardId;
      }

      // 활성화 해제 시 제거, 활성화 상태면 반영
      if (dashboardConfig.enabled) {
        nextOnClick.openDashboard = nextOD;
      } else {
        if (nextOnClick && nextOnClick.openDashboard) delete nextOnClick.openDashboard;
      }
    }

    if (touchedCallFunction) {
      // callFunction 설정 반영
      if (functionCallConfig.enabled && functionCallConfig.functionName) {
        nextOnClick.callFunction = {
          functionName: functionCallConfig.functionName,
          parameters: functionCallConfig.parameters || {},
          confirmMessage: functionCallConfig.confirmMessage || undefined,
          successMessage: functionCallConfig.successMessage || undefined,
          refreshAfter: functionCallConfig.refreshAfter || false,
          note: (functionCallConfig.note && functionCallConfig.note.enabled) ? functionCallConfig.note : undefined
        };
      } else {
        if (nextOnClick && nextOnClick.callFunction) delete nextOnClick.callFunction;
      }
    }

    // on_click가 비어 있으면 null로 정리
    if (nextOnClick && Object.keys(nextOnClick).length === 0) {
      nextOnClick = null;
    }

    // subscribed_variables 구성
    let nextSubscribed = originalSubscribed;
    if (touchedSubscribed) {
      nextSubscribed = {};
      subscribedVariables.forEach(v => {
        if (!v.name) return;
        // 빈 문자열도 유효한 기본값으로 처리 (|| 대신 ?? 사용)
        nextSubscribed[v.name] = { default: v.defaultValue ?? null };
      });
      if (Object.keys(nextSubscribed).length === 0) {
        nextSubscribed = null;
      }
    }

    const payload = {
      on_click: nextOnClick,
      subscribed_variables: nextSubscribed,
      // 편집 여부 플래그 전달(상위에서 조건부 병합에 사용)
      touched_on_click: !!(touchedPublish || touchedOpenDashboard || touchedCallFunction),
      touched_subscribed: !!touchedSubscribed
    };

    if (onChangeRef.current) {
      onChangeRef.current(payload);
    }
  }, [publishVariables, subscribedVariables, dashboardConfig, functionCallConfig, isInitialized, touchedPublish, touchedSubscribed, touchedOpenDashboard, touchedCallFunction, clearVariablesOnDeselect]);
  
  // 발행 변수 업데이트
  const updatePublishVariables = (variables) => {
    setPublishVariables(variables);
    setTouchedPublish(true);
  };
  
  // 구독 변수 업데이트
  const updateSubscribedVariables = (variables) => {
    setSubscribedVariables(variables);
    setTouchedSubscribed(true);
  };
  
  // 대시보드 설정 업데이트
  const updateDashboardConfig = (field, value) => {
    setDashboardConfig(prev => ({ ...prev, [field]: value }));
    setTouchedOpenDashboard(true);
  };

  // DB 함수 호출 설정 업데이트
  const updateFunctionCallConfig = (config) => {
    setFunctionCallConfig(config);
    setTouchedCallFunction(true);
  };

  return (
    <div className="event-tab-panel">
      {/* 대시보드 열기 섹션 */}
      <div className="event-section">
        <div className="section-header" onClick={() => toggleSection('dashboard')}>
          <span className="section-icon">{expandedSections.dashboard ? '▼' : '▶'}</span>
          <h3>대시보드 열기 (옵션)</h3>
          {dashboardConfig.enabled && (
            <span className="status-badge">활성화</span>
          )}
        </div>
        
        {expandedSections.dashboard && (
          <div className="section-content">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={dashboardConfig.enabled}
                  onChange={(e) => updateDashboardConfig('enabled', e.target.checked)}
                />
                클릭 시 대시보드 팝업 열기
              </label>
            </div>
            
            {dashboardConfig.enabled && (
              <>
                <div className="form-group">
                  <label>대시보드 선택</label>
                  {dashboardsLoading ? (
                    <div style={{ fontSize: '13px', color: '#666' }}>대시보드 목록을 불러오는 중...</div>
                  ) : dashboardsError ? (
                    <div style={{ fontSize: '13px', color: '#d9534f' }}>
                      {dashboardsError}
                      <button
                        style={{ marginLeft: '8px' }}
                        onClick={() => {
                          // 강제 재로딩
                          setAvailableDashboards([]);
                          setDashboardsError(null);
                          setDashboardsLoading(false);
                          // 섹션 토글로 useEffect 재실행 유도
                          setExpandedSections(prev => ({ ...prev, dashboard: false }));
                          setTimeout(() => setExpandedSections(prev => ({ ...prev, dashboard: true })), 0);
                        }}
                      >
                        다시 시도
                      </button>
                    </div>
                  ) : (
                    <select
                      value={dashboardConfig.dashboardId}
                      onChange={(e) => updateDashboardConfig('dashboardId', e.target.value)}
                    >
                      <option value="">선택...</option>
                      {availableDashboards.map(d => (
                        <option key={d.id} value={d.dashboard_id || d.id}>
                          {(d.title || d.name || d.dashboard_id || d.id)}
                        </option>
                      ))}
                    </select>
                  )}
                  <small>목록에서 선택하거나 아래 입력란에 직접 ID를 입력할 수 있습니다.</small>
                </div>
                <div className="form-group">
                  <label>대시보드 ID 직접 입력</label>
                  <input
                    type="text"
                    value={dashboardConfig.dashboardId}
                    onChange={(e) => updateDashboardConfig('dashboardId', e.target.value)}
                    placeholder="dashboard_id"
                  />
                  <small>선택된 항목을 덮어쓸 수 있습니다.</small>
                </div>
                
                <div className="form-group">
                  <label>크기 모드</label>
                  <select
                    value={dashboardConfig.sizeMode || 'fixed'}
                    onChange={(e) => updateDashboardConfig('sizeMode', e.target.value)}
                  >
                    <option value="fixed">고정 (직접 지정)</option>
                    <option value="auto">자동 (콘텐츠 맞춤)</option>
                  </select>
                  <small>
                    {dashboardConfig.sizeMode === 'auto' 
                      ? '대상 대시보드의 컴포넌트 영역에 맞춰 팝업 크기가 자동으로 조정됩니다.'
                      : '팝업 크기를 직접 지정합니다.'}
                  </small>
                </div>
                
                <div className="form-group inline-group">
                  {dashboardConfig.sizeMode !== 'auto' && (
                    <>
                      <div>
                        <label>너비</label>
                        <input
                          type="text"
                          value={dashboardConfig.size.width}
                          onChange={(e) => updateDashboardConfig('size', {
                            ...dashboardConfig.size,
                            width: e.target.value
                          })}
                          placeholder="80%"
                          style={{ width: '100px' }}
                        />
                      </div>
                      
                      <div>
                        <label>높이</label>
                        <input
                          type="text"
                          value={dashboardConfig.size.height}
                          onChange={(e) => updateDashboardConfig('size', {
                            ...dashboardConfig.size,
                            height: e.target.value
                          })}
                          placeholder="70%"
                          style={{ width: '100px' }}
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label>위치</label>
                    <select
                      value={dashboardConfig.position}
                      onChange={(e) => updateDashboardConfig('position', e.target.value)}
                    >
                      <option value="center">중앙</option>
                      <option value="top">상단</option>
                      <option value="bottom">하단</option>
                      <option value="left">왼쪽</option>
                      <option value="right">오른쪽</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={dashboardConfig.transparentMode || false}
                      onChange={(e) => updateDashboardConfig('transparentMode', e.target.checked)}
                    />
                    <span>투명 대시보드 모드</span>
                  </label>
                  <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                    활성화 시 팝업 프레임(배경, 테두리, 그림자)이 숨겨지고 차트만 표시됩니다.
                  </small>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* DB 함수 호출 섹션 - 숨김 처리 */}
      {/* <div className="event-section">
        <div className="section-header" onClick={() => toggleSection('function')}>
          <span className="section-icon">{expandedSections.function ? '▼' : '▶'}</span>
          <h3>DB 함수 호출 (Call Function)</h3>
          {functionCallConfig.enabled && functionCallConfig.functionName && (
            <span className="status-badge">활성화 - {functionCallConfig.functionName}</span>
          )}
        </div>

        {expandedSections.function && (
          <div className="section-content">
            <p className="section-description">
              클릭 시 실행할 DB 함수를 설정합니다.
              보안을 위해 미리 정의된 함수만 실행 가능합니다.
            </p>

            <FunctionCallEditor
              config={functionCallConfig}
              onChange={updateFunctionCallConfig}
              tableColumns={tableColumns}
              subscribedVariables={subscribedVariables.reduce((acc, v) => {
                if (v.name) acc[v.name] = { default: v.defaultValue };
                return acc;
              }, {})}
              chartType={chartType}
              sql={sql}
            />
          </div>
        )}
      </div> */}
    </div>
  );
};

export default EventTabPanel;